import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const NEON = "#5BC2DE";
const SESSION_KEY = "csg_site_access";

export function hasSiteAccess() {
  return sessionStorage.getItem(SESSION_KEY) === "granted";
}

export default function Gate({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!password) return;
    setLoading(true);
    try {
      const res = await api.post("/gate/verify", { password });
      if (res.data && res.data.ok) {
        sessionStorage.setItem(SESSION_KEY, "granted");
        onUnlock();
      } else {
        setError("Incorrect password.");
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) setError("Incorrect password.");
      else setError(formatApiError(err.response?.data?.detail) || "Unable to verify. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full bg-black text-white overflow-hidden flex items-center justify-center">
      {/* Subtle background — stadium at night */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=1920&q=85)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />

      {/* Corner accents */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundColor: NEON, opacity: 0.5 }} />
      <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ backgroundColor: NEON, opacity: 0.5 }} />

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative z-10 w-full max-w-xl px-6 md:px-10 text-center"
        data-testid="site-gate"
      >
        <div
          className="font-body text-[11px] tracking-[0.4em] uppercase inline-flex items-center gap-2 mb-6"
          style={{ color: NEON }}
        >
          <Lock size={12} /> Private
        </div>

        <div className="font-display text-2xl md:text-3xl uppercase tracking-wide mb-4">
          <span className="text-white">COMPLETE</span>{" "}
          <span style={{ color: NEON }}>SPORTS</span>{" "}
          <span className="text-white">GROUP</span>
        </div>

        <p className="font-body text-white/70 mt-8 max-w-md mx-auto text-base md:text-lg">
          Enter your access password
        </p>

        <form onSubmit={onSubmit} className="mt-8 max-w-sm mx-auto" data-testid="site-gate-form">
          <div className="flex items-stretch border border-white/20 focus-within:border-[color:var(--csg-accent)] transition-colors"
               style={{ "--csg-accent": NEON }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoFocus
              required
              data-testid="site-gate-input"
              className="flex-1 bg-transparent outline-none px-4 py-4 font-body text-base text-white placeholder:text-white/25"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="site-gate-submit"
              className="px-5 flex items-center gap-2 font-body text-xs tracking-[0.25em] uppercase disabled:opacity-50 transition-all"
              style={{ backgroundColor: NEON, color: "#000" }}
            >
              {loading ? "…" : "Enter"} <ArrowRight size={14} />
            </button>
          </div>
          {error && (
            <div
              className="mt-4 font-body text-sm text-red-400"
              data-testid="site-gate-error"
            >
              {error}
            </div>
          )}
        </form>

        <div className="mt-14 font-body text-[10px] tracking-[0.3em] uppercase text-white/30">
          © {new Date().getFullYear()} Complete Sports Group
        </div>
      </motion.div>
    </div>
  );
}
